import dayjs from 'dayjs';
import { isEmpty, sortBy } from 'rambda';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useThrottledCallback } from 'use-debounce';
import { useDebounceCallback, useTimeout } from 'usehooks-ts';
import { VList, type VListHandle } from 'virtua';
import { useShallow } from 'zustand/react/shallow';
import MessageListItem from '@/components/conversations/conversation-list/message-list-item/message-list-item';
import EmptyLayout from '@/components/layouts/empty-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import useMessageHistory, { fetchMessages } from '@/hooks/conversations/use-message-history';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import { useIsDesktop } from '@/hooks/use-user-platform';
import type { ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import { useAuthStore } from '@/stores/auth';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { useMessageStore } from '@/stores/message';
import { generateSessionKey } from '@/utils/session';
import DateHeader from '../conversation-virtuoso-list/date-header';
import ScrollToEndButton from './scroll-to-end-button';
import ScrollToLastReadButton from './scroll-to-last-read-button';

interface ConversationListProps {
  initialUnreadCount?: number;
  searchId?: number;
  isLoading: boolean;
}

function groupMessagesByDate(messages: ConversationMessageTypeUnion[]) {
  const result = [];
  let currentDate = null;
  for (const item of messages) {
    const messageDate = dayjs(item.CreateTime).format('YYYYMMDD');
    if (messageDate !== currentDate) {
      result.push({
        isDate: true,
        date: messageDate,
      });
      currentDate = messageDate;
    }
    result.push(item);
  }
  return result;
}

export default function ConversationVirtuaList({ initialUnreadCount = 0, searchId, isLoading }: ConversationListProps) {
  const vListRef = useRef<VListHandle>(null);
  const prevGroupedMessagesLength = useRef<number | null>(null);
  const startIndex = useRef<number | null>(null);
  const positionRef = useRef<number>(0);
  const hasLastPositionRef = useRef<boolean>(false);
  const isManualToTailRef = useRef(false);

  const [atEdge, setAtEdge] = useState<'head' | 'tail' | null>('tail');
  const debouncedAtEdge = useDeferredValue(atEdge);

  const [unreadCount, setUnreadCount] = useState(initialUnreadCount);

  const messages = useMessageStore(useShallow((s) => sortBy((i) => i.ID, Array.from(s.messages.values()))));
  const messagesRef = useRef(messages);

  const userId = useAuthStore((s) => s.auth?.UserID);
  const { hydrated: isHydrated, hydrate, clear } = useMessageStore();
  const { type, id } = useConversationStore();
  const isDesktop = useIsDesktop();
  const { isKeyboardVisible } = useAdjustLayout({ inputTargetSelector: '#quill-editor .ql-editor' });

  const {
    fetchPreviousPage,
    fetchNextPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isEnabled: fetchEnabled,
    setEnable: setEnableFetch,
    resetInfiniteQuery,
    setMessageId,
    isLoading: isFetchingHistory,
  } = useMessageHistory(type, id, searchId, !searchId);

  const sessionKey = generateSessionKey(type, id);

  const { setLastPosition, clearLastPosition, lastPositions, lastUnReadSessionIds, setLastUnReadSessionId } =
    useConversationSessionStore();

  const lastPosition = lastPositions[sessionKey] as number;
  const lastUnReadSessionId = lastUnReadSessionIds[sessionKey]?.[userId || 0];
  const [isUnreadMessagesLoaded, setIsUnreadMessagesLoaded] = useState(false);

  const groupedMessages = useMemo(() => groupMessagesByDate(messages), [messages]);
  const groupLengthRef = useRef<number>(groupedMessages.length - 1);

  const getLastReadIndex = useCallback(() => {
    return groupedMessages.findIndex((message) => 'ID' in message && message.ID === lastUnReadSessionId);
  }, [groupedMessages, lastUnReadSessionId]);

  const isAtTail = useCallback(() => {
    if (!vListRef.current) return false;
    return vListRef.current.scrollOffset - vListRef.current.scrollSize + vListRef.current.viewportSize >= -1.5;
  }, []);

  const scrollToIndex = useCallback((index: number, align: 'start' | 'end' | 'center' = 'start', smooth = false) => {
    if (!vListRef.current) return;
    vListRef.current.scrollToIndex(index, { align, smooth });
  }, []);

  const scrollToMessageId = (id: number) => {
    if (id <= 0) return;
    const index = groupedMessages.findIndex((message) => 'ID' in message && message.ID === id);
    scrollToIndex(index, 'start');
  };

  const scrollToTail = () => scrollToIndex(groupLengthRef.current, 'end');

  const debouncedScrollToTail = useDebounceCallback(scrollToTail, 200);

  const updateLastUnReadSessionId = () => {
    const lastUnReadSessionId = messagesRef.current.at(-1)?.ID;
    if (!lastUnReadSessionId) return;
    if (userId) setLastUnReadSessionId(sessionKey, userId, lastUnReadSessionId);
  };

  const adjustScrollAfterPrepend = () => {
    if (atEdge === 'tail') return;

    const prevLen = prevGroupedMessagesLength.current;
    const startIdx = startIndex.current;

    if (prevLen === null || startIdx === null) return;
    if (groupedMessages.length <= prevLen) return;

    const prependedCount = groupedMessages.length - prevLen;
    const newTopIndex = prependedCount - 1 + startIdx;

    prevGroupedMessagesLength.current = null;
    startIndex.current = null;

    scrollToIndex(newTopIndex, 'start');
  };

  const handleOnScroll = (offset: number) => {
    if (!vListRef.current) return;
    positionRef.current = offset;
    const isAtHeadEdge = offset < 5;
    const isAtTailEdge = isAtTail();
    setAtEdge(isAtTailEdge ? 'tail' : isAtHeadEdge ? 'head' : null);
  };

  const handleOnScrollEnd = () => {
    handleLoadNextPage();
    handleLoadPreviousPage();
  };

  useEffect(() => {
    if (searchId) {
      setTimeout(() => {
        setEnableFetch(true);
      }, 2_000);
    }
  }, [searchId]);

  const handleLoadNextPage = () => {
    if (!vListRef.current) return;
    const isAtTailEdge =
      vListRef.current.scrollOffset - vListRef.current.scrollSize + vListRef.current.viewportSize >= -1.5;
    if (isAtTailEdge && !isFetchingNextPage && hasNextPage) {
      ThrottledLoadNextPage();
    }
  };

  const handleLoadPreviousPage = () => {
    if (!vListRef.current) return;
    const isAtHeadEdge = vListRef.current.scrollOffset < 50;
    if (isAtHeadEdge && !isFetchingPreviousPage && hasPreviousPage) {
      prevGroupedMessagesLength.current = groupedMessages.length - 1;
      startIndex.current = vListRef.current?.findStartIndex() + 1;
      ThrottledStartReached();
    }
  };

  const fetchUnreadMessages = async () => {
    if (!lastUnReadSessionId) return;
    await fetchMessages({
      type,
      id,
      anchorMessageId: lastUnReadSessionId,
      direction: 'prev',
      networkOnly: true,
    });
    resetInfiniteQuery(lastUnReadSessionId - 50);
  };

  const checkAndFetchUnreadMessages = () => {
    const index = getLastReadIndex();
    if (index === -1 && unreadCount > 50 && !searchId) {
      fetchUnreadMessages();
      setIsUnreadMessagesLoaded(false);
      return;
    }
  };

  const ThrottledLoadNextPage = useThrottledCallback(async () => {
    if (isLoading || !fetchEnabled || !isHydrated || !hasNextPage || isFetchingNextPage) return;
    await fetchNextPage();
  }, 600);

  const ThrottledStartReached = useThrottledCallback(async () => {
    if (isLoading || !fetchEnabled || !isHydrated || !hasPreviousPage || isFetchingPreviousPage) return;
    await fetchPreviousPage();
  }, 600);

  useTimeout(
    () => {
      if (isKeyboardVisible && atEdge === 'tail') scrollToTail();
    },
    isKeyboardVisible && atEdge === 'tail' ? 100 : null,
  );

  // NOTE: Desktop 初始化未讀訊息載入狀態並載入未讀訊息
  useEffect(() => {
    if (!isDesktop) return;
    setIsUnreadMessagesLoaded(false);
    checkAndFetchUnreadMessages();
  }, [id, unreadCount]);

  // NOTE: Desktop 切換聊天室狀態不會更新，監聽 session id 設回初始狀態
  useEffect(() => {
    if (!isDesktop) return;
    isManualToTailRef.current = false;
    setAtEdge('tail');

    // NOTE: 更換聊天室時更新最後一筆未讀訊息 ID
    return () => {
      updateLastUnReadSessionId();
    };
  }, [id]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (isDesktop) return;
    checkAndFetchUnreadMessages();
    return () => {
      updateLastUnReadSessionId();
    };
  }, []);

  useEffect(() => {
    const index = getLastReadIndex();
    if (index !== -1) {
      setIsUnreadMessagesLoaded(true);
      return;
    }
  }, [groupedMessages]);

  useEffect(() => {
    setUnreadCount(initialUnreadCount);
  }, [initialUnreadCount]);

  useEffect(() => {
    if (isHydrated && !isFetchingHistory && searchId && !isManualToTailRef.current) {
      const timeoutId = setTimeout(() => {
        if (searchId) {
          scrollToMessageId(searchId);
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [isHydrated, isFetchingHistory, searchId]);

  useEffect(() => {
    if (isDesktop) return;
    if (lastPosition !== undefined) hasLastPositionRef.current = true;
    vListRef.current?.scrollTo(lastPosition || 0);
    clearLastPosition(sessionKey);
    return () => setLastPosition(sessionKey, positionRef.current);
  }, []);

  useEffect(() => {
    groupLengthRef.current = groupedMessages.length - 1;

    adjustScrollAfterPrepend();

    if (searchId) return;

    const lastMessage = messages.at(-1);

    if ((atEdge === 'tail' && !hasLastPositionRef.current) || lastMessage?.Status === null) {
      scrollToTail();
      // NOTE: 初次載入聊天室 DOM 可能尚未渲染完成，導致置底失敗，暫時使用 debouncedScrollToTail 補足剩下置底的距離
      debouncedScrollToTail();
    }
  }, [groupedMessages]);

  useEffect(() => {
    setMessageId(searchId);
    isManualToTailRef.current = false;

    if (!searchId && !hasLastPositionRef.current) {
      scrollToTail();
    }
  }, [searchId]);

  return (
    <>
      {!isEmpty(groupedMessages) ? (
        <VList
          className='bg-base-100 flex-1 overscroll-y-none'
          ref={vListRef}
          overscan={4}
          onScroll={handleOnScroll}
          onScrollEnd={handleOnScrollEnd}>
          {groupedMessages.map((item) => {
            if (item.isDate) {
              return (
                <DateHeader
                  key={item.date}
                  date={item.date || ''}
                />
              );
            }
            if ('ID' in item) {
              return (
                <MessageListItem
                  key={item.ID}
                  type='clamped'
                  isHighlighted={searchId === item.ID}
                  message={item}
                />
              );
            }
            return null;
          })}
        </VList>
      ) : searchId ? (
        <LoadingLayout
          className='bg-base-100'
          promise={true}
        />
      ) : (
        <EmptyLayout
          label={type === 'group' ? 'conversation.group.welcome' : 'conversation.message.start-messaging'}
          className='flex-1 bg-base-100'
        />
      )}

      {lastUnReadSessionId !== undefined && isUnreadMessagesLoaded && (
        <ScrollToLastReadButton
          count={unreadCount}
          onClick={() => {
            setAtEdge(null);
            setEnableFetch(false);
            scrollToMessageId(lastUnReadSessionId);
            setUnreadCount(0);
            setTimeout(() => setEnableFetch(true), 500);
          }}
        />
      )}

      <ScrollToEndButton
        isAtTailEdge={debouncedAtEdge === 'tail'}
        onClick={async () => {
          if (searchId && hasNextPage && !isManualToTailRef.current) {
            isManualToTailRef.current = true;
            clear(type, id);
            await hydrate({ type, id });
            resetInfiniteQuery();
            setTimeout(() => scrollToTail(), 200);
          } else {
            scrollToTail();
          }
        }}
      />
    </>
  );
}
